import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import type {
  AccountEntity,
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';
import { v4 as uuidv4 } from 'uuid';

import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import {
  cuentaPorDefecto,
  guardarUltimaCuenta,
  leerUltimaCuenta,
} from '#components/mobile/transactions/pieEditor';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { Hoja } from '#components/mobile/ui/Hoja';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import { Icono } from '#components/mobile/ui/Icono';
import { TecladoImporte } from '#components/mobile/ui/TecladoImporte';
import { color, num, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { registrarUso } from '#uso';

import {
  categoriasFrecuentes,
  centimosDe,
  repetibles,
  teclear,
} from './hoyPuedes';
import type { GastoReciente, Repetible } from './hoyPuedes';

/** Botones de categoría de la hoja (el último es «Otra»). */
const NUM_CATEGORIAS = 7;

type GastoNuevo = {
  cuenta: string;
  fecha: string;
  importe: number;
  categoria: string;
  payee: string | null;
};

/**
 * Apunta el gasto y avisa arriba con «Deshacer» (lo borra). Recuerda la
 * cuenta para el próximo gasto, como el editor completo.
 */
export function useGuardarGasto() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useFormat();

  return async (g: GastoNuevo, nombreCategoria: string) => {
    const id = uuidv4();
    await send('transactions-batch-update', {
      added: [
        {
          id,
          account: g.cuenta,
          date: g.fecha,
          amount: -g.importe,
          category: g.categoria,
          payee: g.payee,
          cleared: false,
          reviewed: true,
        },
      ],
    });
    guardarUltimaCuenta(g.cuenta);
    dispatch(
      addNotification({
        notification: {
          type: 'message',
          timeout: 10000,
          message: t('Saved: {{amount}} in {{category}}', {
            amount: format(g.importe, 'financial'),
            category: nombreCategoria,
          }),
          button: {
            title: t('Undo'),
            action: async () => {
              await send('transactions-batch-update', { deleted: [{ id }] });
            },
          },
        },
      }),
    );
  };
}

type HojaApuntarGastoProps = {
  /** Categorías de «Hoy»: rellenan los botones si hay pocos gastos. */
  idsHoy: readonly string[];
  categorias: readonly CategoryEntity[];
  grupos: readonly CategoryGroupEntity[];
  gastos: readonly GastoReciente[];
  onCerrar: () => void;
};

/**
 * «Apuntar gasto» en dos toques: se escribe el importe con el teclado de la
 * hoja y se toca la categoría (ya está apuntado), o se toca un gasto de
 * «Repetir» sin escribir nada. Cuenta = la última usada; fecha = hoy.
 * «Más detalles» abre el editor completo con lo escrito.
 */
export function HojaApuntarGasto({
  idsHoy,
  categorias,
  grupos,
  gastos,
  onCerrar,
}: HojaApuntarGastoProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const format = useFormat();
  const guardar = useGuardarGasto();
  const { data: cuentasTodas = [] } = useAccounts();
  const { data: payeesById = {} } = usePayeesById();

  const abiertas = useMemo(
    () =>
      (cuentasTodas as AccountEntity[]).filter(a => !a.closed && !a.offbudget),
    [cuentasTodas],
  );
  const [cuentaElegida, setCuentaElegida] = useState<string | null>(null);
  const cuenta =
    cuentaElegida ??
    cuentaPorDefecto({ recordada: leerUltimaCuenta(), abiertas }) ??
    abiertas[0]?.id ??
    null;
  const nombreCuenta = abiertas.find(a => a.id === cuenta)?.name ?? '';

  const [ayer, setAyer] = useState(false);
  const fecha = ayer
    ? monthUtils.subDays(monthUtils.currentDay(), 1)
    : monthUtils.currentDay();

  const [escrito, setEscrito] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [eligiendo, setEligiendo] = useState<'cuenta' | 'fecha' | null>(null);
  const [guardando, setGuardando] = useState(false);

  const porId = useMemo(
    () => new Map(categorias.map(c => [c.id, c])),
    [categorias],
  );
  const validas = useMemo(
    () =>
      new Set(categorias.filter(c => !c.hidden && !c.is_income).map(c => c.id)),
    [categorias],
  );
  const botones = categoriasFrecuentes({
    gastos,
    deHoy: idsHoy,
    validas,
    cuantas: NUM_CATEGORIAS,
  });
  const repetir = repetibles(gastos).filter(r => validas.has(r.category));

  const nombreDe = (id: string) => porId.get(id)?.name ?? '';

  const apuntar = async (g: Omit<GastoNuevo, 'fecha'>, origen: string) => {
    if (guardando || !comprobarPlan()) {
      return;
    }
    setGuardando(true);
    registrarUso('gasto_rapido', { tipo: origen });
    await guardar({ ...g, fecha }, separarEmoji(nombreDe(g.categoria)).resto);
    onCerrar();
  };

  const elegirCategoria = (categoria: string) => {
    const importe = centimosDe(escrito);
    if (!importe) {
      setAviso(t('Type the amount first'));
      return;
    }
    if (!cuenta) {
      setAviso(t('Add an account first'));
      return;
    }
    void apuntar({ cuenta, importe, categoria, payee: null }, 'categoria');
  };

  const repetirGasto = (r: Repetible) => {
    const deEsa = abiertas.some(a => a.id === r.account) ? r.account : cuenta;
    if (!deEsa) {
      setAviso(t('Add an account first'));
      return;
    }
    void apuntar(
      {
        cuenta: deEsa,
        importe: r.importe,
        categoria: r.category,
        payee: r.payee,
      },
      'repetir',
    );
  };

  const otra = () =>
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            categoryGroups: grupos.filter(
              g => !g.is_income,
            ) as CategoryGroupEntity[],
            month: monthUtils.monthFromDate(fecha),
            onSelect: id => {
              if (id) {
                elegirCategoria(id);
              }
            },
          },
        },
      }),
    );

  const masDetalles = () => {
    const params = new URLSearchParams();
    const importe = centimosDe(escrito);
    if (importe) {
      params.set('amount', String(importe / 100));
    }
    if (nombreCuenta) {
      params.set('account', nombreCuenta);
    }
    params.set('date', fecha);
    onCerrar();
    void navigate(`/transactions/new?${params.toString()}`);
  };

  if (eligiendo === 'cuenta') {
    return (
      <HojaAcciones
        titulo={t('Account')}
        onCerrar={() => setEligiendo(null)}
        filas={abiertas.map(a => ({
          id: a.id,
          texto: a.name,
          icono: 'card',
          onPress: () => {
            setCuentaElegida(a.id);
            setEligiendo(null);
          },
        }))}
      />
    );
  }
  if (eligiendo === 'fecha') {
    return (
      <HojaAcciones
        titulo={t('Date')}
        onCerrar={() => setEligiendo(null)}
        filas={[
          {
            id: 'hoy',
            texto: t('Today'),
            icono: 'cal',
            onPress: () => {
              setAyer(false);
              setEligiendo(null);
            },
          },
          {
            id: 'ayer',
            texto: t('Yesterday'),
            icono: 'cal',
            onPress: () => {
              setAyer(true);
              setEligiendo(null);
            },
          },
        ]}
      />
    );
  }

  const etiqueta = {
    ...texto.etiqueta,
    color: color.fg3,
  } as const;

  return (
    <Hoja
      aria-label={t('Add expense')}
      data-testid="hoja-apuntar-gasto"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 12, paddingBottom: 4 }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ ...texto.titulo, fontSize: 18, color: color.fg }}>
            <Trans>Add expense</Trans>
          </Text>
          <Button
            variant="bare"
            onPress={masDetalles}
            style={{
              minHeight: TACTIL,
              ...texto.fila,
              fontSize: 14,
              color: color.accent,
            }}
          >
            <Trans>More details</Trans>
          </Button>
        </View>

        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <Pastilla
            icono="card"
            onPress={() => setEligiendo('cuenta')}
            aria-label={t('Account: {{name}}', { name: nombreCuenta })}
          >
            {nombreCuenta || t('No account')}
          </Pastilla>
          <Pastilla
            icono="cal"
            onPress={() => setEligiendo('fecha')}
            aria-label={t('Date')}
          >
            {ayer ? t('Yesterday') : t('Today')}
          </Pastilla>
        </View>

        <View style={{ alignItems: 'center', gap: 2 }}>
          <Text
            data-testid="importe-gasto"
            style={{
              ...num,
              fontSize: 42,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: escrito ? color.fg : color.fg3,
            }}
          >
            {`${escrito || '0'} €`}
          </Text>
          <Text
            role={aviso ? 'alert' : undefined}
            style={{
              ...texto.secundario,
              fontWeight: 600,
              color: aviso ? color.warn : color.fg3,
            }}
          >
            {aviso ?? t('Type the amount and tap the category')}
          </Text>
        </View>

        {repetir.length > 0 && (
          <View style={{ gap: 8 }}>
            <Text style={etiqueta}>
              <Trans>Repeat</Trans>
            </Text>
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                overflowX: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              {repetir.map(r => {
                const { emoji, resto } = separarEmoji(nombreDe(r.category));
                const sitio = (r.payee && payeesById[r.payee]?.name) || resto;
                return (
                  <Button
                    key={`${r.payee}|${r.category}|${r.importe}`}
                    variant="bare"
                    isDisabled={guardando}
                    onPress={() => repetirGasto(r)}
                    style={{
                      flexShrink: 0,
                      minHeight: TACTIL,
                      padding: '0 12px',
                      borderRadius: radio.pildora,
                      border: `1px solid ${color.line2}`,
                      backgroundColor: color.surface,
                      color: color.fg,
                      gap: 6,
                      ...texto.fila,
                      fontSize: 14,
                    }}
                  >
                    {emoji && <span aria-hidden>{emoji}</span>}
                    <span>{sitio}</span>
                    <span style={{ ...num, color: color.fg2, fontWeight: 600 }}>
                      {format(r.importe, 'financial')}
                    </span>
                  </Button>
                );
              })}
            </View>
          </View>
        )}

        <Text style={etiqueta}>
          <Trans>What was it?</Trans>
        </Text>
        <View
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 6,
          }}
        >
          {botones.map(id => {
            const { emoji, resto } = separarEmoji(nombreDe(id));
            return (
              <BotonCategoria
                key={id}
                emoji={emoji ?? '•'}
                nombre={resto}
                isDisabled={guardando}
                onPress={() => elegirCategoria(id)}
              />
            );
          })}
          <BotonCategoria
            emoji="⋯"
            nombre={t('Other category')}
            isDisabled={guardando}
            onPress={otra}
          />
        </View>

        <TecladoImporte
          onTecla={k => {
            setEscrito(e => teclear(e, k));
            setAviso(null);
          }}
        />
      </View>
    </Hoja>
  );
}

function Pastilla({
  icono,
  children,
  onPress,
  'aria-label': ariaLabel,
}: {
  icono: 'card' | 'cal';
  children: string;
  onPress: () => void;
  'aria-label': string;
}) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      aria-label={ariaLabel}
      style={{
        minHeight: 36,
        padding: '0 12px',
        borderRadius: radio.pildora,
        backgroundColor: color.surface2,
        color: color.fg,
        gap: 6,
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      <Icono nombre={icono} size={16} />
      {children}
      <Icono nombre="down" size={12} strokeWidth={2.5} />
    </Button>
  );
}

function BotonCategoria({
  emoji,
  nombre,
  isDisabled,
  onPress,
}: {
  emoji: string;
  nombre: string;
  isDisabled: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      variant="bare"
      isDisabled={isDisabled}
      onPress={onPress}
      aria-label={nombre}
      style={{
        minHeight: 70,
        borderRadius: radio.boton,
        flexDirection: 'column',
        gap: 4,
        color: color.fg,
        padding: 2,
      }}
    >
      <View
        aria-hidden
        style={{
          width: 42,
          height: 42,
          borderRadius: 21,
          backgroundColor: color.surface2,
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
        }}
      >
        {emoji}
      </View>
      <Text
        style={{
          fontSize: 12,
          fontWeight: 600,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {nombre}
      </Text>
    </Button>
  );
}
